import React, { useRef, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetMilestone } from 'selectors/milestones';
import { makeGetMilestoneTasks } from 'selectors/tasks';

import { updateMilestone, moveMilestone, removeMilestone } from 'actions/milestones';
import { archiveTasks } from 'actions/tasks';
import { showModal } from 'actions/modal';

import { duplicateMilestone } from 'api/milestones-api';

import { useCanUser } from 'shared-hooks/permission';
import { createAlert } from 'services/alert-service';

import Popout from 'components/popout';
import PopoutListActions from 'components/PopoutListActions';
import PromptPopout from 'components/prompt-popout';
import SelectProjectPopout from 'components/SelectProjectPopout';

import IconMoreHoriz from 'icons/icon-more-horiz';
import IconPlus from 'icons/icon-plus';
import IconTransfer from 'icons/icon-transfer';
import IconArchive from 'icons/icon-archive';
import IconRemove from 'icons/icon-remove';
import IconTemplate from 'icons/icon-template';

const DEFAULT_ACTIONS = ['duplicate', 'createTemplate', 'move', 'archive', 'remove', 'removeAndArchiveTasks'];

const MilestoneActions = ({ id, className = 'milestone-actions-holder', innerClassName = 'milestone-actions-trigger', allowedActions, milestoneName, milestoneDescription, milestoneStartDate, milestoneEndDate, milestoneLabels, milestoneProject, milestoneArchived, milestoneCreatedBy, isTiedToTaskGroup, milestoneTasks, onRemove }) => {
    const { t } = useTranslation(['milestone', 'common']);
    const [visible, setVisible] = useState(false);
    const [innerVisible, setInnerVisible] = useState(false);
    const popoutCtrl = useRef(null);
    const canCreateMilestone = useCanUser('createMilestone');
    const canEditMilestone = useCanUser('editMilestone', milestoneCreatedBy);
    const canRemoveMilestone = useCanUser('removeMilestone', milestoneCreatedBy);

    const actions = useMemo(() => {
        return (allowedActions || DEFAULT_ACTIONS).filter(action => {
            switch (action) {
                case 'duplicate':
                    return canEditMilestone;

                case 'createTemplate':
                    return canCreateMilestone;

                case 'move':
                    return canEditMilestone;

                case 'archive':
                    return canEditMilestone;

                case 'remove':
                    return canRemoveMilestone && !isTiedToTaskGroup;

                case 'removeAndArchiveTasks':
                    return canRemoveMilestone;

                default:
                    return true;

            }
        });
    }, [allowedActions, canEditMilestone, canCreateMilestone, canRemoveMilestone, isTiedToTaskGroup]);

    const hidePopout = useCallback(() => {
        popoutCtrl.current?.hide();
    }, [popoutCtrl]);

    const handleTriggerClick = () => {
        popoutCtrl.current?.show();
    };

    const handleShow = useCallback(() => {
        setVisible(true);
    }, []);

    const handleHide = useCallback(() => {
        setVisible(false);
    }, []);

    const handleDuplicateClick = useCallback(() => {
        duplicateMilestone(id);

        hidePopout();
    }, [id, hidePopout]);

    const handleCreateTemplateClick = useCallback(() => {
        showModal('CREATE_TEMPLATE', {
            resourceId: id,
            name: milestoneName,
            type: 'milestone',
            fields: [
                milestoneStartDate ? { type: 'start', value: milestoneStartDate } : null,
                milestoneEndDate ? { type: 'end', value: milestoneEndDate } : null
            ].filter(Boolean),
            disabledOptions: [
                !milestoneDescription ? 'description' : null,
                !milestoneEndDate ? 'end' : null,
                !milestoneStartDate ? 'start' : null,
                !milestoneLabels?.length ? 'labels' : null
            ].filter(Boolean)
        });

        hidePopout();
    }, [id, milestoneName, milestoneDescription, milestoneStartDate, milestoneEndDate, milestoneLabels, hidePopout]);

    const handleArchiveClick = useCallback(() => {
        updateMilestone(id, {
            archived: true,
            archived_on: new Date()
        });

        hidePopout();
    }, [id, hidePopout]);

    const handleUnarchiveClick = useCallback(() => {
        updateMilestone(id, { archived: false });

        hidePopout();
    }, [id, hidePopout]);

    const handleDeleteClick = useCallback(() => {
        removeMilestone(id);

        onRemove && onRemove();

        hidePopout();
    }, [id, hidePopout, onRemove]);

    const handleDeleteAndArchiveClick = useCallback(() => {
        removeMilestone(id);

        if (milestoneTasks.length) {
            archiveTasks({
                task_ids: milestoneTasks.map(task => task.id),
                archived: true
            });
        }

        onRemove && onRemove();

        hidePopout();
    }, [id, milestoneTasks, hidePopout, onRemove]);

    const handleProjectSelect = useCallback(async ({ id: projectId }) => {
        await moveMilestone(id, projectId);

        hidePopout();

        createAlert('success', t('alerts.moved'));
    }, [id, hidePopout, t]);

    const renderAction = useCallback((actionId, onExpand, onCollapse) => {
        switch (actionId) {
            case 'duplicate':
                return (
                    <div className="item-inner" onClick={handleDuplicateClick}>
                        <span className="item-icon"><IconPlus /></span>

                        { t('actions.Duplicate Milestone') }
                    </div>
                );

            case 'createTemplate':
                return (
                    <div className="item-inner" onClick={handleCreateTemplateClick}>
                        <span className="item-icon"><IconTemplate /></span>

                        { t('template:save') }
                    </div>
                );

            case 'move':
                return (
                    <SelectProjectPopout
                        title={ t('actions.move.title') }
                        selected={milestoneProject}
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                        onSelect={handleProjectSelect}
                    >
                        <div className="item-inner">
                            <span className="item-icon"><IconTransfer /></span>

                            { t('actions.move.title') }
                        </div>
                    </SelectProjectPopout>
                );

            case 'archive':
                if (milestoneArchived) {
                    return (
                        <div className="item-inner" onClick={handleUnarchiveClick}>
                            <span className="item-icon"><IconArchive /></span>

                            { t('common:Unarchive') }
                        </div>
                    );
                }

                return (
                    <div className="item-inner" onClick={handleArchiveClick}>
                        <span className="item-icon"><IconArchive /></span>

                        { t('common:Archive') }
                    </div>
                );

            case 'remove':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ t('actions.remove.title') }
                        text={ t('actions.remove.description') }
                        confirmText={ t('common:Delete') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleDeleteClick}>
                            <span className="item-icon"><IconRemove style={{ marginTop: -1 }} /></span>

                            { t('actions.remove.title') }
                        </div>
                    </PromptPopout>
                );

            case 'removeAndArchiveTasks':
                return (
                    <PromptPopout
                        xAlign="right"
                        defaultXPosition="left"
                        title={ t('actions.removeAndArchive.title') }
                        text={ t('actions.removeAndArchive.description') }
                        confirmText={ t('common:Delete') }
                        confirmClassName="btn-caution"
                        inline={true}
                        onShow={onExpand}
                        onHide={onCollapse}
                    >
                        <div className="item-inner" onClick={handleDeleteAndArchiveClick}>
                            <span className="item-icon"><IconRemove style={{ marginTop: -1 }} /></span>

                            { t('actions.removeAndArchive.title') }
                        </div>
                    </PromptPopout>
                );

            default:
                return null;
        }
    }, [t, milestoneProject, milestoneArchived, handleDuplicateClick, handleCreateTemplateClick, handleProjectSelect, handleArchiveClick, handleDeleteClick, handleDeleteAndArchiveClick]);

    if (!actions.length) {
        return null;
    }

    return (
        <div className={className + (visible ? ' visible' : '')} onClick={handleTriggerClick}>
            <span className={innerClassName}><IconMoreHoriz /></span>

            <Popout
                className="xsmall"
                onShow={handleShow}
                onHide={handleHide}
                provideController={popoutCtrl}
             >
                <PopoutListActions actions={actions} renderAction={renderAction} updatePopoutPosition={popoutCtrl.current?.updatePosition} />
            </Popout>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getMilestone = makeGetMilestone();
    const getMilestoneTasks = makeGetMilestoneTasks();

    return ({ milestones, tasks }, ownProps) => {
        const milestone = getMilestone(milestones.items, ownProps.id);
        const milestoneTasks = getMilestoneTasks(tasks.items, ownProps.id);

        return {
            milestoneName: milestone?.name,
            milestoneDescription: milestone?.description,
            milestoneStartDate: milestone?.start,
            milestoneEndDate: milestone?.end,
            milestoneLabels: milestone?.labels,
            milestoneProject: milestone?.project,
            milestoneCreatedBy: milestone?.created_by,
            milestoneArchived: milestone?.archived,
            isTiedToTaskGroup: Boolean(milestone?.task_group),
            milestoneTasks
        };
    };
}

export default connect(makeMapStateToProps)(MilestoneActions);
